/**
 * Client for the KS scoped tool catalog (`POST /agent/<tool>`, contract C2).
 *
 * These tools ride the *scoped plane* (project-scoped rag_api JWT + CASL), unlike
 * qceService which uses the org-plane CMS JWT. We reuse the same short-lived token
 * the RAG chat mints (`purpose: "rag_api"` authorizes chat *and* /agent/*, per
 * contract C3) and derive the `/agent` base from the returned chat URL.
 */
import { getChatToken } from "../chat/chatService";

/** Provenance-only citation as emitted by KS tools (types/chat Citation). */
export interface AgentCitation {
  index: number;
  storyId: string;
  projectId?: string;
  storyTitle?: string;
  chapterTitle?: string;
  speaker?: string;
  startTime: number;
  endTime: number;
  text: string;
  score?: number;
  isChapterSynopsis?: boolean;
}

/** A proposed excerpt match from autocode_seed_expand (Citation + confidence). */
export interface ExcerptMatch extends AgentCitation {
  chunkUuid: string;
  confidence: number;
  rationale?: string;
}

/** A generated theme candidate from generate_themes. */
export interface ThemeCandidate {
  name: string;
  description: string;
  keywords: string[];
  exemplars: {
    text: string;
    rationale?: string;
    source_transcript_id?: string;
    start_offset?: number;
    end_offset?: number;
  }[];
  skill: string;
  evidence: AgentCitation[];
}

export interface GenerateThemesInput {
  storyIds?: string[];
  count?: number;
}

export interface SeedExcerptInput {
  chunkUuid?: string;
  text: string;
  start_offset?: number;
  end_offset?: number;
}

export interface SeedExpandInput {
  themeId: string;
  seedExcerpts: SeedExcerptInput[];
  storyIds?: string[];
}

export interface SemanticSearchInput {
  query: string;
  limit?: number;
}

export interface FindExcerptsInput {
  themeId: string;
  storyIds?: string[];
  limit?: number;
}

export interface FindSimilarInput {
  fromChunkUuid?: string;
  text?: string;
  mode: "within" | "across";
  storyId?: string;
  limit?: number;
}

export interface ListExcerptsInput {
  themeId?: string;
  favoritesOnly?: boolean;
}

/** One row from `list_excerpts` (E2) — a KS `Code` (theme-joined provenance). */
export interface AgentCode {
  id: string;
  theme_id: string;
  theme_name?: string;
  theme_color?: string;
  story_id?: string | null;
  story_title?: string;
  quote: string;
  memo?: string;
  source?: string;
  rating?: number | null;
  chunk_uuid?: string;
  /** Present only when KS left-joins qual_favorites for the calling user. */
  favorite?: boolean;
}

export interface ThemeSpansInput {
  storyId: string;
}

/**
 * Body for the guarded `create_theme` write tool (U4a/U4b). The orchestrator
 * *proposes* a candidate during chat; the write only fires when the user clicks
 * save in the UI, which calls this with `confirm: true`. KS assigns the current
 * project (from the scoped token) and tags `origin: "ai-chat"` server-side.
 */
export interface CreateThemeInput {
  name: string;
  description?: string;
  keywords?: string[];
  exemplars?: ThemeCandidate["exemplars"];
  skill?: string;
  /** Must be true — the explicit user confirmation that authorizes the write. */
  confirm: true;
}

/** KS `create_theme` result — the persisted theme's id/name (shape is loose). */
export interface CreatedTheme {
  id?: string;
  name?: string;
}

/** One excerpt in an `add_excerpts_to_theme` proposal (times in seconds). */
export interface ProposedExcerpt {
  storyId: string;
  storyTitle?: string;
  speaker?: string;
  text: string;
  startTime?: number;
  endTime?: number;
  chunkUuid?: string;
  /** Already coded to the target theme — KS skips it on confirm. */
  alreadyCoded?: boolean;
  /** False when the text isn't found verbatim in the transcript — KS can't file it. */
  locatable?: boolean;
}

/** tool_step `proposal` of the guarded `add_excerpts_to_theme` write tool. */
export interface ExcerptProposal {
  status: "proposed";
  kind: "add_excerpts";
  target: { themeId: string; themeName: string };
  excerpts: ProposedExcerpt[];
  /** Excerpts KS will actually add on confirm (locatable and not already coded). */
  addable?: number;
  alreadyCoded?: number;
  skipped?: number;
}

/** Body for confirming an `add_excerpts_to_theme` proposal (mirrors CreateThemeInput). */
export interface AddExcerptsInput {
  themeId: string;
  excerpts: ProposedExcerpt[];
  /** Must be true — the explicit user confirmation that authorizes the write. */
  confirm: true;
}

/** KS `add_excerpts_to_theme` confirm result. */
export interface AddExcerptsResult {
  status: "added";
  themeId: string;
  added: number;
  alreadyCoded: number;
  skipped: number;
  failed?: number;
}

/** One theme's occurrences within a recording (E3 `theme_spans`). */
export interface ThemeSpan {
  themeId: string;
  spans: { start: number; end: number; codeId?: string }[];
}

/** `${knowledgeServiceUrl}/chat` → `${knowledgeServiceUrl}/agent`. */
function agentBaseFromChatUrl(chatUrl: string): string {
  return chatUrl.replace(/\/chat\/?$/, "");
}

/** Call a scoped agent tool by name and return its bare JSON output (C1 shape). */
async function runAgentTool<T>(orgId: string, projectId: string, tool: string, body: unknown): Promise<T> {
  const { token, chatUrl } = await getChatToken(orgId, projectId);
  const res = await fetch(`${agentBaseFromChatUrl(chatUrl)}/agent/${tool}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      detail = (await res.json())?.error || detail;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail || `Tool "${tool}" failed (${res.status})`);
  }
  return (await res.json()) as T;
}

// ---- A2 orchestrator chat (C1w) --------------------------------------------

/** Events the M2 orchestrator streams over SSE (contract C2 + `tool_step`). We
 *  also surface a synthesized `candidates` event (see streamAgentChat). */
export type AgentChatEvent =
  | { type: "conversation"; conversationId: string }
  | { type: "status"; message: string }
  | { type: "tool_step"; tool: string; status: string; summary?: string }
  | { type: "citations"; citations: AgentCitation[] }
  | { type: "text"; delta: string }
  | { type: "candidates"; candidates: ThemeCandidate[] }
  | { type: "excerpt_proposal"; proposal: ExcerptProposal }
  | { type: "done" }
  | { type: "error"; message: string };

/** Coerce a loose object into a ThemeCandidate; returns null if it has no name. */
function normalizeCandidate(c: any): ThemeCandidate | null {
  if (!c || typeof c !== "object") return null;
  const name = c.name || c.title;
  if (!name) return null;
  const keywords = Array.isArray(c.keywords)
    ? c.keywords
    : typeof c.keywords === "string"
    ? c.keywords
        .split(",")
        .map((k: string) => k.trim())
        .filter(Boolean)
    : [];
  return {
    name,
    description: c.description || c.summary || "",
    keywords,
    exemplars: Array.isArray(c.exemplars) ? c.exemplars : [],
    skill: c.skill || "",
    evidence: Array.isArray(c.evidence) ? c.evidence : [],
  };
}

/**
 * Best-effort extraction of theme candidates from the orchestrator's final text,
 * for the case where they aren't sent as a discrete event: parse a fenced ```json
 * block, else the first JSON array/object, accepting `[]`, `{candidates}` or
 * `{themes}`.
 */
export function parseCandidatesFromText(text: string): ThemeCandidate[] | null {
  if (!text) return null;
  const fence = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const candidatesRaw = (fence ? fence[1] : text).trim();
  const tryParse = (s: string): any => {
    try {
      return JSON.parse(s);
    } catch {
      return null;
    }
  };
  let data = tryParse(candidatesRaw);
  if (!data) {
    const block = candidatesRaw.match(/[[{][\s\S]*[\]}]/);
    if (block) data = tryParse(block[0]);
  }
  if (!data) return null;
  const arr = Array.isArray(data)
    ? data
    : Array.isArray(data.candidates)
    ? data.candidates
    : Array.isArray(data.themes)
    ? data.themes
    : null;
  if (!arr) return null;
  const out = arr.map(normalizeCandidate).filter((c): c is ThemeCandidate => c !== null);
  return out.length ? out : null;
}

/**
 * Stream the A2 orchestrator (`POST /agent/chat`) on the scoped rag_api plane.
 * Forwards each SSE event to `onEvent`; recognizes `candidates` however KS emits
 * them — a discrete `candidates` event, a `tool_step` payload carrying them, or
 * (fallback) parsed from the final accumulated `text` just before `done`.
 */
export async function streamAgentChat(
  orgId: string,
  projectId: string,
  query: string,
  onEvent: (e: AgentChatEvent) => void,
  signal?: AbortSignal,
  // Echo the id from the `conversation` event to continue a thread server-side
  // (history + prompt-cache reuse); omit on the first turn. Mirrors RAG /chat.
  conversationId?: string
): Promise<void> {
  const { token, chatUrl } = await getChatToken(orgId, projectId);
  const res = await fetch(`${agentBaseFromChatUrl(chatUrl)}/agent/chat`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ query, orgId, projectId, ...(conversationId ? { conversationId } : {}) }),
    signal,
  });
  if (!res.ok || !res.body) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body?.error || detail;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail || `Agent chat failed (${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let accumulatedText = "";
  let sawCandidates = false;
  let done = false;

  const emit = (e: AgentChatEvent) => {
    if (e.type === "candidates") sawCandidates = true;
    if (e.type === "text") accumulatedText += e.delta;
    if (e.type === "done") {
      // Fallback: derive candidates from the final text if none were streamed.
      if (!sawCandidates) {
        const parsed = parseCandidatesFromText(accumulatedText);
        if (parsed) onEvent({ type: "candidates", candidates: parsed });
      }
      done = true;
    }
    onEvent(e);
  };

  const flushFrame = (frame: string) => {
    let evName: string | undefined;
    const dataLines: string[] = [];
    for (const line of frame.split("\n")) {
      if (line.startsWith("event:")) evName = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
    }
    if (dataLines.length === 0) return;
    const raw = dataLines.join("\n");
    if (raw === "[DONE]") {
      emit({ type: "done" });
      return;
    }
    let payload: any = raw;
    try {
      payload = JSON.parse(raw);
    } catch {
      /* plain-text delta */
    }
    const type = evName || payload?.type;
    // A tool_step (or any frame) may carry candidates alongside its own type.
    const inlineCandidates = Array.isArray(payload?.candidates) ? payload.candidates : null;
    if (inlineCandidates) {
      const norm = inlineCandidates.map(normalizeCandidate).filter((c: ThemeCandidate | null) => c !== null);
      if (norm.length) emit({ type: "candidates", candidates: norm as ThemeCandidate[] });
    }
    switch (type) {
      case "conversation":
        emit({ type: "conversation", conversationId: String(payload?.conversationId ?? "") });
        break;
      case "status":
        emit({ type: "status", message: String(payload?.message ?? payload?.status ?? payload ?? "Thinking…") });
        break;
      case "tool_step": {
        emit({
          type: "tool_step",
          tool: String(payload?.tool ?? ""),
          status: String(payload?.status ?? ""),
          summary: payload?.summary ? String(payload.summary) : undefined,
        });
        // A guarded write tool (create_theme) rides its drafted theme on the
        // tool_step `proposal` ({ status:"proposed", theme }). Surface it as a
        // candidate so the inline "Save theme" CTA renders — otherwise the agent
        // proposes into the void and tells the user to save from a UI that shows
        // nothing.
        // Only kind-less proposals are create_theme; add_excerpts_to_theme rides
        // `kind: "add_excerpts"` and gets its own card, never a Save-theme one.
        const proposal = payload?.proposal;
        if (proposal && proposal.status === "proposed") {
          if (proposal.kind === "add_excerpts") {
            if (proposal.target?.themeId && Array.isArray(proposal.excerpts))
              emit({ type: "excerpt_proposal", proposal: proposal as ExcerptProposal });
          } else if (!proposal.kind && proposal.theme) {
            const cand = normalizeCandidate(proposal.theme);
            if (cand) emit({ type: "candidates", candidates: [cand] });
          }
        }
        break;
      }
      case "citations":
        emit({ type: "citations", citations: (payload?.citations ?? []) as AgentCitation[] });
        break;
      case "candidates":
        if (Array.isArray(payload?.candidates)) {
          const norm = payload.candidates.map(normalizeCandidate).filter((c: ThemeCandidate | null) => c !== null);
          emit({ type: "candidates", candidates: norm as ThemeCandidate[] });
        }
        break;
      case "text":
      case "delta":
        emit({ type: "text", delta: String(payload?.content ?? payload?.delta ?? payload?.text ?? payload ?? "") });
        break;
      case "done":
        emit({ type: "done" });
        break;
      case "error":
        emit({ type: "error", message: String(payload?.message ?? "Agent chat failed") });
        break;
      default:
        break;
    }
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done: streamDone, value } = await reader.read();
    if (streamDone) break;
    buffer += decoder.decode(value, { stream: true });
    let sep = buffer.indexOf("\n\n");
    while (sep !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      flushFrame(frame);
      sep = buffer.indexOf("\n\n");
    }
  }
  if (buffer.trim()) flushFrame(buffer);
  // Ensure a terminal done (and candidate fallback) even if the server omitted it.
  if (!done) emit({ type: "done" });
}

export const agentService = {
  generateThemes: (orgId: string, projectId: string, input: GenerateThemesInput = {}) =>
    runAgentTool<ThemeCandidate[]>(orgId, projectId, "generate_themes", input),
  autocodeSeedExpand: (orgId: string, projectId: string, input: SeedExpandInput) =>
    runAgentTool<ExcerptMatch[]>(orgId, projectId, "autocode_seed_expand", input),
  semanticSearch: (orgId: string, projectId: string, input: SemanticSearchInput) =>
    runAgentTool<AgentCitation[]>(orgId, projectId, "semantic_search", input),
  findExcerptsForTheme: (orgId: string, projectId: string, input: FindExcerptsInput) =>
    runAgentTool<ExcerptMatch[]>(orgId, projectId, "find_excerpts_for_theme", input),
  listExcerpts: (orgId: string, projectId: string, input: ListExcerptsInput = {}) =>
    runAgentTool<AgentCode[]>(orgId, projectId, "list_excerpts", input),
  themeSpans: (orgId: string, projectId: string, input: ThemeSpansInput) =>
    runAgentTool<ThemeSpan[]>(orgId, projectId, "theme_spans", input),
  findSimilar: (orgId: string, projectId: string, input: FindSimilarInput) =>
    runAgentTool<ExcerptMatch[]>(orgId, projectId, "find_similar", input),
  // Guarded write (U4b): persist a proposed theme on explicit user confirmation.
  createTheme: (orgId: string, projectId: string, input: CreateThemeInput) =>
    runAgentTool<CreatedTheme>(orgId, projectId, "create_theme", input),
  // Guarded write: add proposed excerpts to a theme on explicit user confirmation.
  addExcerptsToTheme: (orgId: string, projectId: string, input: AddExcerptsInput) =>
    runAgentTool<AddExcerptsResult>(orgId, projectId, "add_excerpts_to_theme", input),
};
